/**
 * Teacher Scheduling — pure derivations for the week board + attention rail.
 *
 * Everything here is a pure function over plain data so the board's judgments
 * (what conflicts, what repeats, why a teacher is disqualified) are testable
 * without Firestore, a browser, or a rendered component. The page does the I/O;
 * this file does the thinking.
 *
 * ── THE ONE TIME RULE, AND IT IS THE OPPOSITE OF THE USUAL ONE ──
 *
 * `sourceEvents` store start/end as STUDIO-LOCAL-AS-UTC: a 5:15 PM lesson is
 * written `2026-08-31T17:15:00Z`. That is deliberate and load-bearing — the
 * generator's availability scorer reads `getUTCHours()`, and
 * `eventOverlapsWallWindows` compares against "HH:MM" strings, so the UTC
 * fields ARE the wall clock. Both producers agree: syncSourceCalendar's
 * parseIcsDate floating-time normalization and
 * scripts/migration/materializeLessonSourceEvents.js.
 *
 * THEREFORE: read these Dates with getUTC* and format them with
 * `timeZone: 'UTC'`. Passing one through `America/Chicago` shifts it five hours
 * EARLIER and renders a 5:15 PM lesson as "12:15 PM". The pre-rebuild page did
 * exactly that on every row (fixed 2026-08-26 — the ids carry the true time,
 * e.g. `s-2026-2027-1-1715-HLDC` is 17:15, so the data was never wrong, only
 * the label). Day keys survived it because every lesson sits between 11:00 and
 * 21:15 wall time and so never crosses midnight under a -5/-6 shift; times did
 * not. Do not "fix" this by introducing a timezone — the absence of one is the
 * correct behavior for this collection.
 */

/** Firestore/day-grid key order. Matches teacherAvailability.js DAY_KEYS. */
export const DAY_KEYS = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
];

export const DAY_LABELS = {
	sunday: 'Sun',
	monday: 'Mon',
	tuesday: 'Tue',
	wednesday: 'Wed',
	thursday: 'Thu',
	friday: 'Fri',
	saturday: 'Sat',
};

/**
 * Which weekday the board's week starts on. 0 = Sunday.
 *
 * Sunday-first per the rebuild card's note 4: Mon→Sun buries the studio's
 * biggest teaching day at the bottom of the board, and the studio week
 * effectively starts Sunday. This is the whole switch — set it to 1 for
 * Monday-first and every week calculation, header and rail follows.
 */
export const WEEK_STARTS_ON = 0;

/* ────────────────────────────── day keys ────────────────────────────── */

/** "2026-08-31" → Date at UTC noon (noon avoids every DST edge). */
export const dayKeyToDate = (dayKey) => {
	const [year, month, day] = String(dayKey).split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

/**
 * Today's studio date from the browser's LOCAL clock. Not dateToDayKey: that
 * reads UTC, which is already tomorrow after 7pm Central, so "has this day
 * passed" would flip a day early every evening. The portal runs in the studio.
 */
export const localTodayKey = (now = new Date()) =>
	`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/** Date → "2026-08-31", read in UTC (see the time rule above). */
export const dateToDayKey = (date) => {
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const addDays = (dayKey, delta) => {
	const date = dayKeyToDate(dayKey);
	date.setUTCDate(date.getUTCDate() + delta);
	return dateToDayKey(date);
};

/** A Firestore Timestamp (or anything with toDate) → day key. */
export const timestampToDayKey = (timestamp) => {
	if (!timestamp?.toDate) return null;
	return dateToDayKey(timestamp.toDate());
};

/** 0..6 for the weekday of a day key. */
export const weekdayIndex = (dayKey) => dayKeyToDate(dayKey).getUTCDay();

export const dayKeyName = (dayKey) => DAY_KEYS[weekdayIndex(dayKey)];

/** The day key that starts the board week containing `dayKey`. */
export const startOfWeek = (dayKey, weekStartsOn = WEEK_STARTS_ON) => {
	const offset = (weekdayIndex(dayKey) - weekStartsOn + 7) % 7;
	return addDays(dayKey, -offset);
};

/** The seven day keys of the board week containing `dayKey`. */
export const weekDayKeys = (dayKey, weekStartsOn = WEEK_STARTS_ON) => {
	const start = startOfWeek(dayKey, weekStartsOn);
	return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

/**
 * Every day key from `startKey` to `endKey` inclusive — the board's RANGE
 * scope, as opposed to `weekDayKeys`' seven.
 *
 * Bounded at 400 days as a runaway guard: the range comes from two date inputs
 * a human types, and a mistyped year would otherwise ask the board to render
 * tens of thousands of day rows and hang the tab. A season is ~300 days, so the
 * cap cannot bite a real range.
 *
 * Returns [] when the range is inverted rather than looping forever, because
 * `endKey < startKey` is reachable simply by editing the start date first.
 */
export const dayKeysBetween = (startKey, endKey, maxDays = 400) => {
	if (!startKey || !endKey || endKey < startKey) return [];
	const out = [];
	let cursor = startKey;
	while (cursor <= endKey && out.length < maxDays) {
		out.push(cursor);
		cursor = addDays(cursor, 1);
	}
	return out;
};

/* ──────────────────────────── formatting ──────────────────────────── */

/**
 * Wall-clock time of a stored event. UTC on purpose — see the time rule.
 */
export const formatEventTime = (timestamp) => {
	if (!timestamp?.toDate) return '—';
	return timestamp.toDate().toLocaleTimeString('en-US', {
		timeZone: 'UTC',
		hour: 'numeric',
		minute: '2-digit',
	});
};

export const formatEventRange = (startTs, endTs) => {
	if (!startTs?.toDate || !endTs?.toDate) return '—';
	return `${formatEventTime(startTs)} – ${formatEventTime(endTs)}`;
};

/** "Sun Sep 20" for a day key. */
export const formatDayKeyShort = (dayKey) =>
	dayKeyToDate(dayKey).toLocaleDateString('en-US', {
		timeZone: 'UTC',
		weekday: 'short',
		month: 'short',
		day: 'numeric',
	});

/** "Sep 20" for a day key (the day-column subtitle). */
export const formatDayKeyDate = (dayKey) =>
	dayKeyToDate(dayKey).toLocaleDateString('en-US', {
		timeZone: 'UTC',
		month: 'short',
		day: 'numeric',
	});

/** "Week of Sep 14 – 20, 2026" */
export const formatWeekLabel = (weekStartKey) => {
	const start = dayKeyToDate(weekStartKey);
	const end = dayKeyToDate(addDays(weekStartKey, 6));
	const sameMonth = start.getUTCMonth() === end.getUTCMonth();
	const startText = start.toLocaleDateString('en-US', {
		timeZone: 'UTC',
		month: 'short',
		day: 'numeric',
	});
	const endText = end.toLocaleDateString('en-US', {
		timeZone: 'UTC',
		...(sameMonth ? {} : { month: 'short' }),
		day: 'numeric',
	});
	return `Week of ${startText} – ${endText}, ${end.getUTCFullYear()}`;
};

/* ──────────────────────────── locations ──────────────────────────── */

export const normalizeLocation = (value) => {
	if (!value) return null;
	const text = String(value).toLowerCase();
	if (text.includes('hldc')) return 'HLDC';
	if (text.includes('lakeville')) return 'LAKEVILLE';
	return null;
};

export const locationKey = (value) => normalizeLocation(value) || 'UNSPECIFIED';

export const LOCATION_LABELS = {
	HLDC: 'HLDC · Burnsville',
	LAKEVILLE: 'Lakeville',
	UNSPECIFIED: 'Unspecified',
};

/** Stable board order: HLDC first, then Lakeville, then anything else. */
const LOCATION_RANK = { HLDC: 0, LAKEVILLE: 1, UNSPECIFIED: 2 };
export const compareLocations = (a, b) =>
	(LOCATION_RANK[a] ?? 3) - (LOCATION_RANK[b] ?? 3) || a.localeCompare(b);

/**
 * Can this teacher work this location? Mirrors teacherMatchesLocation in the
 * generator: no declared locations means no restriction.
 */
export const teacherMatchesLocation = (teacher, location) => {
	const target = normalizeLocation(location);
	if (!target) return true;
	if (Array.isArray(teacher?.locations) && teacher.locations.length > 0) {
		return teacher.locations.map(normalizeLocation).filter(Boolean).includes(target);
	}
	if (teacher?.location) return normalizeLocation(teacher.location) === target;
	return true;
};

/* ─────────────────────────── availability ─────────────────────────── */

const timeToMinutes = (text) => Number(text.slice(0, 2)) * 60 + Number(text.slice(3, 5));

const eventMinutes = (timestamp) => {
	const date = timestamp.toDate();
	return date.getUTCHours() * 60 + date.getUTCMinutes();
};

/**
 * Does [start,end) overlap any {start,end} "HH:MM" window? Same arithmetic as
 * functions/teacherAvailability.js eventOverlapsWallWindows — deliberately
 * mirrored rather than shared, because that module is Node-side and pulling it
 * into the bundle would drag firebase-admin with it. Kept in lockstep by
 * schedulingModel.test.js, which pins the same cases.
 */
export const overlapsWindows = (startTs, endTs, windows) => {
	if (!Array.isArray(windows) || windows.length === 0) return false;
	if (!startTs?.toDate || !endTs?.toDate) return false;
	const startMin = eventMinutes(startTs);
	const endMin = eventMinutes(endTs);
	return windows.some((window) => {
		if (!window?.start || !window?.end) return false;
		return startMin < timeToMinutes(window.end) && endMin > timeToMinutes(window.start);
	});
};

/**
 * How a teacher stands against one day, ignoring the specific lessons.
 * Returns { off, windows, blocks } where `off` is a hard whole-day no.
 */
export const teacherDayStanding = (teacher, dayKey) => {
	const name = dayKeyName(dayKey);
	const availability = teacher?.availability?.[name];
	return {
		off: availability === false,
		windows: Array.isArray(availability) ? availability : null,
		blocks: Array.isArray(teacher?.unavailableBlocks?.[name])
			? teacher.unavailableBlocks[name]
			: null,
	};
};

/**
 * Student name out of a synced calendar summary.
 *
 * Real shapes from `sourceEvents`, which come from the studio calendar:
 *   "Sarah Conner (Private Lessons | Lakeville) "
 *   "Katherine Bowman (Private Lesson | HLDC) "
 *
 * The parenthetical is the lesson type and venue, both of which the card
 * already shows, so it is noise in a per-lesson row. Everything is defensive:
 * a summary with no parenthetical is used whole, and an empty or missing one
 * falls back to a dash rather than rendering "undefined" at a teacher.
 */
export const lessonStudentName = (summary) => {
	if (typeof summary !== 'string') return '—';
	const withoutParens = summary.replace(/\s*\([^)]*\)\s*/g, ' ');
	const cleaned = withoutParens.replace(/\s+/g, ' ').trim();
	return cleaned || summary.trim() || '—';
};

/* ─────────────────────────── board building ─────────────────────────── */

/**
 * Group events into day → location blocks, attaching each event's assignment.
 *
 * A "block" is one day at one location — the unit the generator assigns a
 * single teacher to, and therefore the unit the board renders as one card.
 */
export const buildBlocks = ({ events, assignmentsByEventId, dayKeys = null }) => {
	const allowed = dayKeys ? new Set(dayKeys) : null;
	const byKey = new Map();

	for (const event of events) {
		const dayKey = timestampToDayKey(event.start);
		if (!dayKey) continue;
		if (allowed && !allowed.has(dayKey)) continue;
		const location = locationKey(event.location);
		const key = `${dayKey}::${location}`;
		if (!byKey.has(key)) {
			byKey.set(key, { key, dayKey, location, events: [], assignments: [] });
		}
		const block = byKey.get(key);
		block.events.push(event);
		const assignment = assignmentsByEventId?.get(event.id) || null;
		if (assignment) block.assignments.push(assignment);
	}

	for (const block of byKey.values()) {
		block.events.sort(
			(a, b) => a.start.toDate().getTime() - b.start.toDate().getTime()
		);
		block.startTs = block.events[0]?.start || null;
		block.endTs = block.events[block.events.length - 1]?.end || null;
		block.lessonCount = block.events.length;

		// A block's teacher is the one its assignments agree on; a split block
		// (mid-day handoff, or a timeslot rule firing) reports null and is shown
		// as mixed rather than silently picking the first one.
		const teacherIds = new Set(block.assignments.map((a) => a.teacherId || null));
		block.teacherId = teacherIds.size === 1 ? [...teacherIds][0] : null;
		block.isMixed = teacherIds.size > 1;
		block.isLocked =
			block.assignments.length > 0 && block.assignments.every((a) => a.isLocked);
		block.isPartiallyLocked =
			!block.isLocked && block.assignments.some((a) => a.isLocked);
		block.manualOverride = block.assignments.some((a) => a.manualOverride);
		block.notes = block.assignments.find((a) => a.notes)?.notes || '';

		// Per-lesson rows for the card's expandable list. Built here rather than
		// in the component because the event→assignment join is model work, and
		// because `block.assignments` is a flat array that has already lost which
		// event each assignment belongs to — a card cannot recover the pairing.
		//
		// The teacher is resolved PER LESSON, not from block.teacherId: a timeslot
		// rule can hand a single slot to a different teacher, which is exactly the
		// case this list exists to make visible (the card can only say "Split day").
		block.lessons = block.events.map((event) => {
			const assignment = assignmentsByEventId?.get(event.id) || null;
			return {
				eventId: event.id,
				startTs: event.start || null,
				endTs: event.end || null,
				studentName: lessonStudentName(event.summary),
				teacherId: assignment?.teacherId || null,
				isLocked: Boolean(assignment?.isLocked),
				manualOverride: Boolean(assignment?.manualOverride),
			};
		});
	}

	return [...byKey.values()].sort(
		(a, b) => a.dayKey.localeCompare(b.dayKey) || compareLocations(a.location, b.location)
	);
};

/** Blocks keyed by day, in board location order — what the week board renders. */
export const groupBlocksByDay = (blocks, dayKeys) => {
	const byDay = new Map(dayKeys.map((key) => [key, []]));
	for (const block of blocks) {
		if (byDay.has(block.dayKey)) byDay.get(block.dayKey).push(block);
	}
	return dayKeys.map((dayKey) => ({ dayKey, blocks: byDay.get(dayKey) }));
};

/* ──────────────────────────── flags ──────────────────────────── */

export const FLAG_CONFLICT = 'conflict';
export const FLAG_REPEAT = 'repeat';
export const FLAG_UNASSIGNED = 'unassigned';

/**
 * Soft conflicts: a block assigned to a teacher who is not available for it.
 *
 * "Soft" is the generator's word, not a judgment about severity — the scorer
 * PENALISES these rather than forbidding them, so a block can legitimately end
 * up here when a teacher was the only candidate. That is exactly why they must
 * be surfaced loudly for a human instead of being silently accepted.
 */
export const blockConflicts = ({ block, teacher, timeOffDates }) => {
	if (!teacher || !block.teacherId) return [];
	const reasons = [];
	const standing = teacherDayStanding(teacher, block.dayKey);

	if (timeOffDates?.has(`${block.teacherId}::${block.dayKey}`)) {
		reasons.push('has time off booked this day');
	}
	if (standing.off) {
		reasons.push(`is marked unavailable on ${DAY_LABELS[dayKeyName(block.dayKey)]}days`);
	}
	if (standing.blocks) {
		const clashing = block.events.filter((event) =>
			overlapsWindows(event.start, event.end, standing.blocks)
		);
		if (clashing.length > 0) {
			reasons.push(
				`has a hard block over ${clashing.length} lesson${clashing.length === 1 ? '' : 's'}`
			);
		}
	}
	if (standing.windows) {
		const outside = block.events.filter(
			(event) => !overlapsWindows(event.start, event.end, standing.windows)
		);
		if (outside.length > 0) {
			reasons.push(
				`is only available part of the day — ${outside.length} lesson${outside.length === 1 ? '' : 's'} outside those hours`
			);
		}
	}
	if (!teacherMatchesLocation(teacher, block.location)) {
		reasons.push(`does not teach at ${LOCATION_LABELS[block.location] || block.location}`);
	}
	return reasons;
};

/**
 * Rotation memory: who had this weekday+location the previous two weeks.
 *
 * Built from the whole assignment set, not the visible week — the rotation rule
 * spans weeks, so a board showing one week still needs the two before it.
 */
export const buildRotationHistory = (blocks) => {
	const history = new Map(); // "weekday::location::dayKey" → teacherId
	for (const block of blocks) {
		if (!block.teacherId) continue;
		history.set(`${weekdayIndex(block.dayKey)}::${block.location}::${block.dayKey}`, block.teacherId);
	}
	return history;
};

export const rotationMemoryFor = (block, history) => {
	const slot = `${weekdayIndex(block.dayKey)}::${block.location}`;
	return {
		lastWeek: history.get(`${slot}::${addDays(block.dayKey, -7)}`) || null,
		priorWeek: history.get(`${slot}::${addDays(block.dayKey, -14)}`) || null,
	};
};

/** A repeat is the same teacher on the same weekday+location as last week. */
export const isRotationRepeat = (block, history) => {
	if (!block.teacherId) return false;
	return rotationMemoryFor(block, history).lastWeek === block.teacherId;
};

/**
 * A flag on a date that has already gone by. Only a REPEAT settles: once the
 * lesson has happened there is nothing left to change (Anthony, 2026-10-22:
 * "when the date has already passed, I'm happy with it staying
 * yellow-highlighted. I no longer need to change that teacher"). It keeps its
 * colour on the card and leaves the rail, the flagged count and the card's
 * change controls. A past CONFLICT or UNASSIGNED day still matters (the pay
 * stamp credits the scheduled teacher), so those stay live.
 */
export const isSettledFlag = (flag) => Boolean(flag && flag.past);

/** The flags that still ask for something. */
export const liveFlags = (flags) => (flags || []).filter((flag) => !isSettledFlag(flag));

/**
 * Everything wrong with one block, ranked. Conflicts outrank repeats outrank
 * unassigned, which is the rail's sort order and the card's border colour.
 * `todayKey` is the studio's LOCAL date; a repeat on a day before it is
 * marked `past` (isSettledFlag). Without it nothing is past.
 */
export const flagBlock = ({ block, teachersById, timeOffDates, rotationHistory, todayKey = null }) => {
	const flags = [];
	const teacher = block.teacherId ? teachersById.get(block.teacherId) : null;

	const conflicts = blockConflicts({ block, teacher, timeOffDates });
	if (conflicts.length > 0) {
		flags.push({
			type: FLAG_CONFLICT,
			severity: 3,
			teacherName: teacher?.name || block.teacherId,
			reasons: conflicts,
		});
	}
	if (!block.teacherId && !block.isMixed) {
		flags.push({ type: FLAG_UNASSIGNED, severity: 2, reasons: ['No teacher assigned'] });
	}
	if (isRotationRepeat(block, rotationHistory)) {
		flags.push({
			type: FLAG_REPEAT,
			severity: 1,
			teacherName: teacher?.name || block.teacherId,
			reasons: ['Same teacher as last week on this weekday and location'],
			past: Boolean(todayKey) && block.dayKey < todayKey,
		});
	}
	return flags.sort((a, b) => b.severity - a.severity);
};

/** The loudest flag on a block, or null — drives the card's visual state. */
export const topFlag = (flags) => (flags.length > 0 ? flags[0] : null);

/* ───────────────────── disqualification reasons ───────────────────── */

/**
 * Why each teacher is or is not a candidate for a block — the mockup's best
 * interaction (rebuild card note 2). Returns one row per teacher with a
 * human reason when they are out.
 *
 * Note the card's own warning: a disabled <option> renders low-contrast, so a
 * load-bearing reason must ALSO appear outside the select. The picker shows
 * these inline and the conflict strip repeats the one that matters.
 */
export const candidateTeachersForBlock = ({
	block,
	teachers,
	timeOffDates,
	rotationHistory,
}) => {
	const memory = rotationMemoryFor(block, rotationHistory);

	return teachers
		.filter((teacher) => teacher.active !== false)
		.map((teacher) => {
			const standing = teacherDayStanding(teacher, block.dayKey);
			const reasons = [];
			let hard = false;

			if (timeOffDates?.has(`${teacher.id}::${block.dayKey}`)) {
				reasons.push('day off');
				hard = true;
			}
			if (!teacherMatchesLocation(teacher, block.location)) {
				reasons.push(`not at ${block.location === 'HLDC' ? 'HLDC' : 'Lakeville'}`);
				hard = true;
			}
			if (standing.blocks) {
				const clashing = block.events.filter((event) =>
					overlapsWindows(event.start, event.end, standing.blocks)
				);
				if (clashing.length > 0) {
					reasons.push(describeWindows(standing.blocks));
					hard = true;
				}
			}
			if (standing.off) {
				reasons.push(`doesn't do ${DAY_LABELS[dayKeyName(block.dayKey)]}days`);
			}
			if (standing.windows) {
				const outside = block.events.filter(
					(event) => !overlapsWindows(event.start, event.end, standing.windows)
				);
				if (outside.length > 0) {
					reasons.push(`only ${describeWindows(standing.windows)}`);
				}
			}
			if (memory.lastWeek === teacher.id) {
				reasons.push('had this slot last week');
			}

			return {
				teacher,
				id: teacher.id,
				name: teacher.name || teacher.id,
				hard,
				soft: !hard && reasons.length > 0,
				eligible: reasons.length === 0,
				reason: reasons.join(' · ') || null,
			};
		})
		.sort((a, b) => {
			if (a.hard !== b.hard) return a.hard ? 1 : -1;
			if (a.soft !== b.soft) return a.soft ? 1 : -1;
			return a.name.localeCompare(b.name);
		});
};

/** [{start:'15:00',end:'17:00'}] → "3:00–5:00 pm" */
export const describeWindows = (windows) => {
	if (!Array.isArray(windows) || windows.length === 0) return '';
	const fmt = (text) => {
		const hour = Number(text.slice(0, 2));
		const minute = text.slice(3, 5);
		const suffix = hour >= 12 ? 'pm' : 'am';
		const display = hour % 12 === 0 ? 12 : hour % 12;
		return `${display}:${minute}${suffix}`;
	};
	return windows.map((w) => `${fmt(w.start)}–${fmt(w.end)}`).join(', ');
};

/* ──────────────────────────── load ──────────────────────────── */

/**
 * A teacher's lesson cap for a run, or null for uncapped ("leave it open").
 *
 * null is the DEFAULT and it must stay that way: a teacher with no cap field
 * is unlimited, so adding the feature changes nothing for anyone until a cap
 * is deliberately set. Zero is a real value (assign none) and is deliberately
 * NOT treated as "unset" — `?? null` rather than `|| null`, because `0 || null`
 * is null and would silently uncap a teacher who was capped at zero.
 */
export const teacherCap = (teacher) => {
	const raw = teacher?.maxLessons;
	if (raw === null || raw === undefined || raw === '') return null;
	const n = Number(raw);
	return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * Per-teacher counts across the blocks given — the rail's "Rotation & load".
 *
 * Carries the cap alongside the count because the cap is SOFT: the generator
 * prefers other teachers but will exceed it rather than leave a block
 * unassigned (Anthony, 2026-08-30), so a run can legitimately end up over.
 * `overBy` is what makes that visible instead of silent — which was the
 * explicit condition on choosing a soft cap at all.
 */
export const teacherLoad = ({ blocks, teachers }) => {
	const counts = new Map(teachers.map((t) => [t.id, { blocks: 0, lessons: 0 }]));
	for (const block of blocks) {
		if (!block.teacherId) continue;
		if (!counts.has(block.teacherId)) counts.set(block.teacherId, { blocks: 0, lessons: 0 });
		const entry = counts.get(block.teacherId);
		entry.blocks += 1;
		entry.lessons += block.lessonCount;
	}
	return teachers
		.filter((teacher) => teacher.active !== false)
		.map((teacher) => {
			const entry = counts.get(teacher.id) || { blocks: 0, lessons: 0 };
			const cap = teacherCap(teacher);
			return {
				teacher,
				...entry,
				cap,
				overBy: cap === null ? 0 : Math.max(0, entry.lessons - cap),
				isOverCap: cap !== null && entry.lessons > cap,
			};
		});
};

/**
 * Rail contents for a set of blocks, loudest first.
 * `scope` is carried through so the rail can say what range it covers — the
 * "Lock all clean" copy depends on it (rebuild card note 6).
 */
export const buildAttentionItems = ({ blocks, teachersById, timeOffDates, rotationHistory, todayKey = null }) => {
	const items = [];
	for (const block of blocks) {
		const flags = flagBlock({ block, teachersById, timeOffDates, rotationHistory, todayKey });
		for (const flag of liveFlags(flags)) {
			items.push({ block, flag, key: `${block.key}::${flag.type}` });
		}
	}
	return items.sort(
		(a, b) => b.flag.severity - a.flag.severity || a.block.dayKey.localeCompare(b.block.dayKey)
	);
};

/** Blocks with no LIVE flags — what "Lock all clean" acts on. A settled (past) repeat counts as clean. */
export const cleanBlocks = ({ blocks, teachersById, timeOffDates, rotationHistory, todayKey = null }) =>
	blocks.filter(
		(block) =>
			block.teacherId &&
			liveFlags(flagBlock({ block, teachersById, timeOffDates, rotationHistory, todayKey })).length === 0
	);
